import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import Link from "next/link";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import BecomeInstructorForm from "@/components/BecomeInstructorForm";
import webengage from "@/webengage";
import Breadcrumb from "@/components/BreadCrumb";
import Head from "next/head";

const reasonCards = [
  {
    title: "The #1 platform to monetize your expertise!",
    content:
      "Launch Live Cohort Based Courses that attract lakhs from around the world",
    iconUrl: "/reason-card-icon-1.svg",
  },
  {
    title: "Try LiveKit",
    content:
      "World's Best teaching platform for Live Classes. Register now to experience unparalleled and seamless live class interactions on LiveKit!",
    iconUrl: "/reason-card-icon-3.svg",
  },
  {
    title: "You focus on the teaching, We do the boring stuff",
    content:
      "Wow your learners with great teaching while we take care of Sales, Marketing, Operations, CRM, Finance Technology, Design, and everything else!",
    iconUrl: "/reason-card-icon-2.svg",
  },
];

const onboardingCards = [
  {
    title: `Click on "Get Started"`,
    content: `To start your journey with us, click on the "Get Started" button. This will take you to a form where you can provide your initial details.`,
    asset: "/onboarding-1.lottie",
  },
  {
    title: "Select Your Finance Category",
    content:
      "Once you begin the form, you'll be asked to select the specific finance category you wish to teach. Choose the area that best matches your expertise and passion.",
    asset: "/onboarding-2.lottie",
  },
  {
    title: "Provide Your Social Media Details",
    content:
      "Next, you'll need to share your social media handles. This helps us verify your identity and understand your reach and engagement with your audience.",
    asset: "/onboarding-3.lottie",
  },
  {
    title: "Our Team Will Connect with You",
    content:
      "After submitting the form, our team will review your information and get in touch with you to discuss the next steps. We look forward to welcoming you to our teaching community!",
    asset: "/onboarding-4.lottie",
  },
];

const howToBeginTabs = [
  "Get Started",
  "Select Category",
  "Provide Details",
  "We Will Connect You",
];

const socialProofs = [
  {
    metricNumber: "40K+ ",
    metricTitle: " Hours",
    description: "Of LIVE learning",
  },
  {
    metricNumber: "3 ",
    metricTitle: " Years",
    description: "Of building community",
  },
  {
    metricNumber: "15 ",
    metricTitle: " Lakh+",
    description: "learners attended free Masterclasses",
  },
  {
    metricNumber: "100",
    metricTitle: "+",
    description: "Experts onboarded to deliver best content",
  },
  {
    metricNumber: "50",
    metricTitle: "K+",
    description: "Students learnt and became successful traders",
  },
];

const BecomeInstructor = () => {
  const [activeFAQ, setActiveFAQ] = React.useState<number[]>([0]);
  const contentRef = React.useRef<(HTMLDivElement | null)[]>([]);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const [isActive, setIsActive] = React.useState(0);
  const button1Ref = React.useRef<HTMLButtonElement>(null);
  const button2Ref = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [isCTAVisible, setIsCTAVisible] = React.useState(true);

  const scrollToForm = () => {
    button2Ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleFAQ = (index: number) => {
    if (isMobile) {
      setActiveFAQ((prev) => {
        if (prev.includes(index)) {
          return prev.filter((item) => item !== index);
        }

        return [index];
      });
    }
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsCTAVisible(true);
          } else {
            setIsCTAVisible(false);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (button1Ref.current) {
      observer.observe(button1Ref.current);
    }
    if (button2Ref.current) {
      observer.observe(button2Ref.current);
    }
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (button1Ref.current) {
        observer.unobserve(button1Ref.current);
      }
      if (button2Ref.current) {
        observer.unobserve(button2Ref.current);
      }
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  return (
    <>
      <Head>
        <title>
          Become an Instructor | Teach Trading Online with AstroLearn
        </title>
        <meta
          name="description"
          content="Join Tradewise as an instructor and share your expertise in trading. Teach courses to eager learners and build your online teaching career with us."
        />
      </Head>

      <Layout isContainerHidden>
        <motion.section
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ duration: 0.355, type: "tween" }}
          viewport={{ once: true }}
          className="md:bg-[#EDEDED]"
        >
          <div className="container mx-auto max-md:p-0 text-foreground text-center md:flex flex-row-reverse items-center justify-between md:text-start sm:px-4 md:px-6 relative">
            <Breadcrumb classNames="absolute top-2 lg:top-4 left-4 md:left-6 max-md:text-xs" />
            <div className="max-md:bg-[#EDEDED] max-md:w-full">
              <Image
                src="/become-instructor-hero.png"
                width={487}
                height={600}
                alt="potrait of a woman"
                className="object-contain max-md:w-[240px] md:w-[280px] lg:w-[400px] mx-auto"
              />
            </div>
            <div className="max-md:p-4">
              <h1 className="font-bold text-xl my-4 max-md:mt-6 md:text-3xl lg:text-5xl">
                Come teach with us
              </h1>
              <p className="tracking-wide my-4 md:my-6 lg:my-8 lg:text-xl md:max-w-[500px]">
                Become an instructor and change lives including your own.
              </p>
              <Button
                onClick={scrollToForm}
                ref={button1Ref}
                id="button1"
                className="text-white font-bold rounded-lg py-4 w-full md:w-fit leading-5 md:px-10 lg:px-20 lg:py-6"
              >
                Get started
              </Button>
            </div>
          </div>
        </motion.section>

        {/* ----------- Reasons section ----------- */}
        <motion.section
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ duration: 0.355, type: "tween" }}
          viewport={{ once: true }}
          className="my-16 md:my-24 text-center container mx-auto sm:px-4 md:px-6"
        >
          <h2 className="text-xl text-foreground font-bold md:text-3xl lg:text-[40px]">
            So many reasons to start
          </h2>

          <div className="md:flex max-lg:flex-wrap justify-center gap-6 lg:gap-4 md:mt-10">
            {reasonCards.map((item, i) => (
              <div
                key={i}
                className={cn(
                  "px-[18px] py-[22px] lg:py-8 rounded-[13px] max-md:mt-4 md:w-1/3 md:max-lg:w-[48%]",
                  i === 0
                    ? "bg-[#E9DEFF4D]"
                    : i === 1
                    ? "bg-[#D2E4FE4D]"
                    : "bg-[#FBE3E34D]"
                )}
              >
                <Image
                  src={item.iconUrl}
                  width={80}
                  height={80}
                  alt="icon"
                  className="mx-auto mix-blend-multiply h-[80px]"
                />
                <h4 className="text-foreground text-2xl font-bold my-2 max-md:max-w-[325px] mx-auto md:text-lg lg:leading-snug lg:max-w-[250px]">
                  {item.title}
                </h4>
                <p
                  className={cn(
                    "text-[#0A2133CC] mx-auto text-balance font-medium md:text-sm",
                    i === 1 ? "md:max-w-[318px]" : ""
                  )}
                >
                  {item.content}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* ----------- Impact Section ----------- */}
        <section className="w-full relative py-2 md:py-4 mt-3 md:mt-5 container mx-auto sm:px-4 md:px-6">
          <h2 className="text-xl text-foreground font-bold md:text-3xl lg:text-[40px] text-center">
            Creating impact around the world with
          </h2>
          <div className="grid grid-cols-2 gap-2.5 place-items-stretch mx-auto mt-4 md:mt-10">
            {socialProofs.map((proof, index) => {
              const isLast = index === socialProofs.length - 1;
              return (
                <div
                  key={index}
                  className={cn(
                    "bg-[#EFF2F666] hover:bg-[#eeeeee66] p-4 sm:p-5 md:p-6 rounded-lg flex flex-col gap-y-1.5 justify-center items-center w-full transition-all",
                    isLast && "col-span-2"
                  )}
                >
                  <div className="flex gap-1 text-center text-balance text-xl sm:text-2xl md:text-3xl font-bold text-foreground/85 tracking-wide">
                    <motion.div
                      initial={{ opacity: 0, translateY: 40 }}
                      whileInView={{ opacity: 1, translateY: 0 }}
                      transition={{
                        duration: 0.3,
                      }}
                      viewport={{ once: true }}
                    >
                      {proof.metricNumber}
                    </motion.div>
                    <motion.div
                      initial={{ translateY: 30 }}
                      whileInView={{ translateY: 0 }}
                      transition={{
                        duration: 0.3,
                        delay: 0.3,
                      }}
                      viewport={{ once: true }}
                    >
                      {proof.metricTitle}
                    </motion.div>
                  </div>
                  <p className="max-sm:text-sm text-balance text-center text-foreground/40 tracking-wide">
                    {proof.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ----------- Onboarding section ----------- */}
        <motion.section
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ duration: 0.355, type: "tween" }}
          viewport={{ once: true }}
          className="my-16 md:my-24"
        >
          {/* Mobile Version */}
          {isMobile &&
            onboardingCards.map((item, index) => {
              const isActive = activeFAQ.includes(index);

              return (
                <div>
                  <h2 className="text-xl text-foreground font-bold text-center">
                    How to begin?
                  </h2>
                  <div
                    className="container mx-auto sm:px-4 my-4 p-5 bg-[#041C330F] cursor-pointer overflow-hidden rounded-xl transition-all duration-200 ease-linear"
                    onClick={() => toggleFAQ(index)}
                    key={index}
                  >
                    <div className="flex justify-between items-center">
                      <p className="text-[#041C33] font-semibold leading-tight">
                        {item.title}
                      </p>
                      <ChevronDown
                        size={25}
                        className={cn(
                          "transition-transform duration-200 ease-linear",
                          isActive ? "rotate-180" : "rotate-0",
                          isMobile ? "block" : "hidden"
                        )}
                      />
                    </div>
                    <div
                      ref={(el) => (contentRef.current[index] = el)}
                      className={cn(
                        "overflow-hidden transition-max-height duration-200 ease-linear",
                        isActive && isMobile ? "max-h-[300px]" : "max-h-0"
                      )}
                      style={{
                        maxHeight:
                          isActive || !isMobile
                            ? `${contentRef.current[index]?.scrollHeight}px`
                            : "0px",
                      }}
                    >
                      <DotLottieReact
                        src={item.asset}
                        loop
                        autoplay
                        speed={0.8}
                        height={200}
                      />
                      <p
                        data-state={isActive || !isMobile ? "open" : "closed"}
                        className="my-2 text-[15px] font-medium leading-relaxed text-[#041C3399] data-[state=open]:opacity-100 data-[state=closed]:opacity-0 transition-opacity duration-300 ease-linear"
                      >
                        {item.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

          {/* Desktop version */}
          <div className="bg-[#FDF2CD33] py-10 hidden md:block">
            <h2 className="text-3xl text-foreground font-bold text-center lg:text-[40px] mt-8">
              How to begin?
            </h2>
            <div className="max-w-[950px] mx-auto flex justify-center items-center overflow-x-auto cursor-pointer mt-10">
              {howToBeginTabs.map((item: any, index: number) => {
                return (
                  <div
                    key={index}
                    className={cn(
                      "w-full border-b-2 border-gray-200 text-balance font-bold pb-2 transition-all duration-200 ease-in",
                      isActive === index
                        ? "text-black border-b-4 border-black"
                        : "text-gray-400"
                    )}
                    onClick={() => {
                      setIsActive(index);
                    }}
                  >
                    <p className="text-center mx-auto text-[19px]">{item}</p>
                  </div>
                );
              })}
            </div>
            {onboardingCards.map(
              (item, index) =>
                isActive === index && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.35, type: "tween" }}
                    viewport={{ once: true }}
                    className="container mx-auto mt-6 flex gap-6 items-center px-10 lg:px-32"
                  >
                    <div className="text-xl">
                      <h4 className="font-bold">{item.title}</h4>
                      <p className="text-[#2A2A2A] font-medium mt-4">
                        {item.content}
                      </p>
                    </div>
                    <div className="">
                      <DotLottieReact
                        src={item.asset}
                        loop
                        autoplay
                        speed={0.8}
                        height={200}
                        className="w-[430px]"
                      />
                    </div>
                  </motion.div>
                )
            )}
          </div>
        </motion.section>

        {/* ----------- CTA section ----------- */}
        <motion.section
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ duration: 0.355, type: "tween" }}
          viewport={{ once: true }}
          className="text-center my-16 md:my-24 container mx-auto sm:px-4 md:px-6"
        >
          <h2 className="text-foreground text-xl md:text-3xl lg:text-[40px] font-bold my-4">
            You won&apos;t have to do it alone
          </h2>
          <div className="md:flex items-center justify-between md:mt-10">
            <DotLottieReact
              src="become-instructor-2.lottie"
              loop
              autoplay
              height={150}
              className="md:w-[60%]"
            />
            <div className="md:w-[75%] md:text-start">
              <p className="tracking-wide text-[#161C2BCC] md:text-xl leading-9 font-medium">
                Our Instructor Support Team is here to address all your queries.
                If you have additional questions, our Teachers Onboarding Team
                will reach out to you within 5-7 working days. For any immediate
                queries, please reach out to us on WhatsApp by{" "}
                <Link
                  href="https://wa.me/+919056187997"
                  target="_blank"
                  className="underline text-[#0085FF]"
                  onClick={() => {
                    const _webengage = webengage();
                    if (_webengage) {
                      _webengage.track("Contact Us", {
                        source: "Tradewise",
                        type: "Chat",
                      });
                    }
                  }}
                >
                  clicking here
                </Link>
                .
              </p>
            </div>
          </div>
          <div className="md:hidden">
            <h3 className="text-foreground text-xl md:text-2xl font-bold mt-16">
              Become an instructor today
            </h3>
            <p className="my-4 leading-6 tracking-wide text-[#161C2B] mx-auto max-w-[300px]">
              Join one of the world&apos;s largest online learning marketplaces.
            </p>
            <Button
              onClick={scrollToForm}
              id="button2"
              className="text-white font-bold rounded-lg py-[14px] leading-5 w-full"
            >
              Get started
            </Button>
          </div>
        </motion.section>

        <div ref={button2Ref} />

        <BecomeInstructorForm />

        <div ref={footerRef} />

        {/* ----------- Fixed CTA ----------- */}
        <div
          className={cn(
            "fixed md:hidden container sm:px-4 md:px-6 mx-auto z-1 bottom-0 inset-x-0 px-4 py-3 pb-1 transition-all duration-200 ease-out",
            isCTAVisible ? "translate-y-full " : "translate-y-0"
          )}
        >
          <Button
            onClick={scrollToForm}
            className="text-white font-bold rounded-lg py-[14px] leading-5 w-full border-0"
          >
            Get started
          </Button>
        </div>
      </Layout>
    </>
  );
};

export default BecomeInstructor;
